/** 大神迹:D15 神像落成 / D16 世界树生长 / D17 流星雨。 */
import { Graphics, Sprite } from 'pixi.js';
import { BaseEffect, EffectGroup } from './base';
import { eventPool } from '../engine/ParticlePool';
import { sky } from '../engine/SkyRenderer';
import { scene } from '../engine/Scene';
import { TEX } from '../engine/Tex';
import { P } from '../engine/Palette';
import { easing } from '../engine/Easing';
import { LW, LH } from '../engine/Stage';
import { rand, pick } from '../engine/util';
import { sfx } from '../engine/Sfx';

/** D15 神像落成(ceremony;神像剪影由建筑层揭出,此处演仪式) */
export class StatueRiseEffect extends BaseEffect {
  id = 'statue';
  exclusiveGroup: EffectGroup = 'ceremony';
  private ring = new Graphics(); private cx = LW * 0.5; private cy = 0; private phase = 0; private dustT = 0;
  onStart() { this.dur = 12; this.tail = 2; this.cy = scene.island.surfaceYAt(this.cx); this.g.addChild(this.ring); this.ring.blendMode = 'add'; scene.crowd?.pray(3); sfx.play('carve'); sfx.play('carve', { delay: 1.4 }); }
  onUpdate(dt: number) {
    this.ring.clear();
    if (this.t < 9) { const rot = this.t * 1.5; for (let i = 0; i < 3; i++) this.ring.arc(this.cx, this.cy, 60 + i * 14, rot + i, rot + i + 2).stroke({ width: 2, color: P.divine, alpha: 0.6 * (1 - this.t / 9) }); }
    if (this.t < 6) {
      this.stage.shake.shake(1, 0.2);
      this.dustT -= dt;
      if (this.dustT <= 0) { this.dustT = 0.25; eventPool?.spawn({ x: this.cx + rand(-30, 30), y: this.cy, vx: rand(-30, 30), vy: rand(-60, -20), life: 1, scale: 0.03, tint: sky.silhouette, alpha: 0.6, texture: TEX.glow(), fade: 'quad' }); }
    }
    if (this.phase === 0 && this.t >= 6) { this.phase = 1; this.stage.shake.shake(3, 0.4); this.dustRing(); scene.crowd?.gather(this.cx, 4); sfx.play('bell'); scene.buildings?.revealStatue(); }
  }
  private dustRing() { for (let i = 0; i < 24; i++) { const a = (i / 24) * 6.28; eventPool?.spawn({ x: this.cx, y: this.cy, vx: Math.cos(a) * 120, vy: Math.sin(a) * 40 - 10, life: 1, scale: 0.03, tint: P.divine, alpha: 0.7, blend: 'add', texture: TEX.glow(), fade: 'quad' }); } }
}

/** D16 世界树生长(树与果实常驻,不随效果销毁) */
export class WorldTreeEffect extends BaseEffect {
  id = 'world_tree';
  private tree = new Graphics(); private fruits: Sprite[] = []; private cx = LW * 0.5; private cy = 0;
  onStart() { this.dur = 15; this.tail = 1; this.cy = scene.island.surfaceYAt(this.cx); scene.stage.layers.mainIsland.addChild(this.tree); scene.crowd?.gather(this.cx, this.dur); sfx.play('chime'); }
  onUpdate() {
    const grow = easing.quadOut(Math.min(1, this.t / 6));
    this.tree.clear();
    this.branch(this.tree, this.cx, this.cy, -Math.PI / 2, 150 * grow, 12 * grow, 0, sky.silhouette);
    if (this.t > 7 && this.fruits.length < 12 && Math.random() < 0.3) {
      const s = new Sprite(TEX.glow()); s.anchor.set(0.5); s.blendMode = 'add'; s.tint = P.ember; s.scale.set(0.04);
      s.position.set(this.cx + rand(-120, 120), this.cy - rand(120, 240));
      scene.stage.layers.mainIsland.addChild(s); this.fruits.push(s);
    }
  }
  private branch(g: Graphics, x: number, y: number, ang: number, len: number, w: number, depth: number, sil: number) {
    // 冠形修正:旧版在每个 depth≥2 的**中间节点**都盖一颗正圆——枝干结构全被
    // 圆斑打断,读出来是"分子结构图"。真树冠 = 叶量聚在**枝端**;故只在递归
    // 终止处(末梢)放一小簇 2~3 颗压扁椭圆,枝干骨架完整保留。
    if (depth > 4 || len < 8) {
      // 首验簇径 15/11 偏小,末梢像伞菌;放大到 22/16 并聚拢重叠成一团冠体
      for (let i = 0; i < 3; i++) {
        const a = ang + (i - 1) * 0.8;
        g.ellipse(x + Math.cos(a) * 9, y + Math.sin(a) * 6, 22 - i * 3, 16 - i * 2.5);
      }
      g.fill(sil);
      return;
    }
    const x2 = x + Math.cos(ang) * len, y2 = y + Math.sin(ang) * len;
    g.moveTo(x, y).lineTo(x2, y2).stroke({ width: Math.max(1, w), color: sil });
    const s = 0.5 + 0.12 * depth;
    this.branch(g, x2, y2, ang - s, len * 0.72, w * 0.7, depth + 1, sil);
    this.branch(g, x2, y2, ang + s, len * 0.72, w * 0.7, depth + 1, sil);
  }
  onDestroy() { /* 树/果实挂在 mainIsland(非 this.g),常驻 */ }
}

/** D17 流星雨(投币爆发专属) */
export class MeteorShowerEffect extends BaseEffect {
  id = 'meteor_shower';
  private next = 0;
  private streaks: { parts: Sprite[]; x: number; y: number; vx: number; vy: number }[] = [];
  onStart() { this.dur = 30; scene.crowd?.broadcast('idle', this.dur); }
  onUpdate(dt: number) {
    this.next -= dt;
    if (this.next <= 0 && this.t < this.dur) { this.next = rand(0.4, 1.2); this.spawn(); }
    for (let i = this.streaks.length - 1; i >= 0; i--) {
      const m = this.streaks[i];
      for (let j = m.parts.length - 1; j > 0; j--) m.parts[j].position.copyFrom(m.parts[j - 1].position);
      m.parts[0].position.set(m.x, m.y);
      m.x += m.vx * dt; m.y += m.vy * dt;
      if (m.y > LH + 40 || m.x < -80) { m.parts.forEach((p) => p.destroy()); this.streaks.splice(i, 1); }
    }
  }
  private spawn() {
    if (Math.random() < 0.3) sfx.play('meteor');
    // 亮度分层:多数是暗色远流星,~1/4 是"头流星"(track≥1.4)——实核头部、
    // 更亮更快。同批流星全等亮会读成"下小雪",层次感来自明暗差而非数量。
    const track = pick([0.6, 0.8, 1.0, 1.4]);
    const lead = track >= 1.4;
    const x = rand(LW * 0.4, LW * 1.05), y = rand(-30, LH * 0.1);
    const parts: Sprite[] = [];
    for (let i = 0; i < 8; i++) {
      const s = new Sprite(i === 0 && lead ? TEX.core() : TEX.glow());
      s.anchor.set(0.5); s.blendMode = 'add';
      s.tint = lead ? 0xeaf2ff : P.moon;
      s.scale.set((0.035 - i * 0.002) * track * (lead ? 1.25 : 1));
      s.alpha = (lead ? 0.95 : 0.55) - i * 0.06;
      s.position.set(x, y); this.g.addChild(s); parts.push(s);
    }
    this.streaks.push({ parts, x, y, vx: -760 * track, vy: 520 * track });
  }
}
